<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        :drawer="drawer"
        :persistent="true"
        max-width="720px"
        color="error"
    >
        <!-- Header -->
        <div slot="header" class="header">
            {{ $t('terminals.heading_register') }}

            <v-btn
                dark
                icon
                @click="logout"
            >
                <v-icon>fal fa-sign-out</v-icon>
            </v-btn>
        </div>

        <!-- Content -->
        <div
            v-if="!terminalTypes.filter(t => t.enabled).length"
            slot="content"
            class="terminal-register-content"
        >
            <v-alert
                class="my-4"
                text
                outlined
                :prominent="$refs.dialog && !$refs.dialog.isFullscreen"
                type="error"
            >
                {{ $t('general.not_possible_from_web_app') }}
            </v-alert>
        </div>

        <div
            v-else
            slot="content"
            class="terminal-register-content"
        >
            <v-tabs
                v-if="terminalTypes.filter(t => t.enabled).length > 1"
                v-model="selectedType"
                class="tabs"
                background-color="transparent"
            >
                <v-tab
                    v-for="(terType, index) in terminalTypes"
                    :key="terType.type"
                    :ref="`${terType.type}Tab`"
                    :class="{ 'first-tab': index == 0 }"
                    :href="`#${terType.type}`"
                >
                    {{ getTerminalTypeLabel(terType.type) }}
                </v-tab>
            </v-tabs>

            <v-tabs-items
                v-model="selectedType"
                class="tabs-items"
                touchless
            >
                <v-tab-item
                    v-for="(terType, index) in terminalTypes"
                    :key="index"
                    :value="terType.type"
                    :transition="false"
                >
                    <div v-if="terminalTypes.find(t => t.type === selectedType)?.error">
                        <v-alert
                            class="my-4"
                            text
                            outlined
                            :prominent="$refs.dialog && !$refs.dialog.isFullscreen"
                            type="error"
                        >
                            {{ $t(`terminals.${terminalTypes.find(t => t.type === selectedType)?.error}`) }}
                        </v-alert>
                    </div>

                    <div v-else-if="terType.type === 'cashRegister'">
                        <v-alert
                            class="my-4"
                            text
                            outlined
                            :prominent="$refs.dialog && !$refs.dialog.isFullscreen"
                            type="error"
                        >
                            <div>
                                {{ $t('cash_register.registration_needed') }}
                                <br><br>
                                {{ $tc('cash_register.registered_number', cashRegisters.length ? 2 : 1, { value: cashRegisters.length } ) }}
                            </div>
                        </v-alert>

                        <v-text-field
                            v-model="cashRegisterForm.name"
                            :error-messages="cashRegisterForm.$errors.name"
                            :label="$t('cash_register.name')"
                            @blur="cashRegisterForm.validate('name')"
                            @input="delete cashRegisterForm.$errors.name"
                        />

                        <v-text-field
                            v-if="locale === 'sr'"
                            v-model="cashRegisterForm.reseller_hash"
                            class="mb-4"
                            :error-messages="cashRegisterForm.$errors.reseller_hash"
                            :label="$t('cash_register.password')"
                            @blur="cashRegisterForm.validate('reseller_hash')"
                            @input="delete cashRegisterForm.$errors.reseller_hash"
                        />

                        <v-text-field
                            v-if="locale === 'ba'"
                            v-model="cashRegisterForm.activation_code"
                            class="mb-4"
                            :error-messages="cashRegisterForm.$errors.activation_code"
                            :label="$t('cash_register.activation_code')"
                            @blur="cashRegisterForm.validate('activation_code')"
                            @input="delete cashRegisterForm.$errors.activation_code"
                        />

                        <template v-if="locale === 'sr'">
                            <v-switch
                                v-model="cashRegisterForm.limit_payments"
                                class="mt-0 mb-6 mx-1"
                                hide-details
                                :label="$t('cash_register.limited_payments')"
                                inset
                            />

                            <v-alert
                                v-show="cashRegisterForm.limit_payments"
                                type="warning"
                                text
                                outlined
                                transition="slide-y-transition"
                            >
                                {{ $t('cash_register.limited_payments_description') }}
                            </v-alert>
                        </template>
                    </div>

                    <div v-else>
                        <v-text-field
                            v-model="terminalForm.name"
                            class="mt-4"
                            :error-messages="terminalForm.$errors.name"
                            :label="$t('general.name')"
                            @blur="terminalForm.validate('name')"
                            @input="delete terminalForm.$errors.name"
                        />

                        <v-select
                            v-if="terType.type === 'orderingDevice'"
                            v-model="terminalForm.cash_register_id"
                            :error-messages="terminalForm.$errors.cash_register_id"
                            :items="cashRegisters"
                            :label="$t('general.cash_register')"
                            :item-text="item => item.getFullName()"
                            item-value="id"
                            clear-icon="fal fa-times"
                        />
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </div>

        <div
            slot="footer"
            class="d-flex justify-center flex-wrap-reverse wfull"
        >
            <v-btn
                v-if="allows('nav_access_admin')"
                class="ma-2"
                color="#757575"
                outlined
                @click="$router.push({ name: 'admin' }).catch(err => {})"
            >
                {{ $t('general.administration') }}
            </v-btn>

            <v-btn
                color="error"
                class="ma-2"
                depressed
                :disabled="!enableRegister"
                :loading="terminalForm.$busy || cashRegisterForm.$busy"
                @click="register"
            >
                <span v-if="selectedType === 'cashRegister'">{{ $t('terminals.cash_register_register') }}</span>
                <span v-else-if="selectedType === 'orderingDevice'">{{ $t('terminals.ordering_device_register') }}</span>
                <span v-else>{{ $t('terminals.kitchen_display_system_register') }}</span>
            </v-btn>
        </div>
    </cod-dialog>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import UserService from '@/services/UserService';
import ApiService from '@/services/ApiService';
import CashRegisterService from '@/services/CashRegisterService';
import TerminalService from '@/services/TerminalService';
import CashRegister from '@/models/CashRegister';
import Terminal from '@/models/Terminal';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';

export default {
    components: {
        CodDialog
    },

    props: {
        show: {
            type: Boolean,
            default: false
        }
    },

    created() {
        this.cashRegisters = CashRegister.all()
        if(this.terminalTypes.length > 0) {
            this.selectedType = this.terminalTypes[0].type
        }

        this.cashRegisterForm = this.createCashRegisterForm()
        this.terminalForm = this.createTerminalForm()
    },

    data() {
        return {
            showDialog: false,
            drawer: null,

            cashRegisterForm: this.createCashRegisterForm(),
            terminalForm: this.createTerminalForm(),

            cashRegisters: [],
            selectedType: Terminal.TYPE_CASH_REGISTER,
        }
    },

    validations() {
        return {
            cashRegisterForm: {
                name: { required: required },
                activation_code: { requiredIf: requiredIf(form => locale === 'ba') },
                reseller_hash: { requiredIf: requiredIf(form => locale === 'sr') },
            },
            terminalForm: {
                name: { required: required },
                cash_register_id: { requiredIf: requiredIf(form => this.selectedType === Terminal.TYPE_ORDERING_DEVICE) },
            }
        }
    },

    watch: {
        show(val) {
            this.showDialog = val;
            this.drawer = null;
        },

        showDialog(val) {
            this.$emit('update:show', val);

            if(val) {
                this.cashRegisters = CashRegister.all()
            }
        },

        selectedType(val) {
            this.cashRegisterForm = this.createCashRegisterForm()
            this.terminalForm = this.createTerminalForm()
        },
    },

    computed: {
        ...mapGetters(['modules']),

        terminalTypes() {
            // invalid case, should not happen
            if(CashRegister.current()) {
                return [
                    { type: Terminal.TYPE_CASH_REGISTER, enabled: true, error: 'limit_reached' },
                    { type: Terminal.TYPE_ORDERING_DEVICE, enabled: !isAllegra, error: 'limit_reached' },
                    { type: Terminal.TYPE_KITCHEN_DISPLAY_SYSTEM, enabled: !isAllegra, error: 'limit_reached' }
                ]
            }

            const isMobile = this.device.platform === 'android' || this.device.platform === 'ios'
            const isWeb = this.device.platform === 'web'

            let cashRegistersError = ''
            const cashRegistersLimit = this.tenant.status !== 'permanently_active'
                ? (this.tenant.active_tenant_contracts?.reduce((acc, cur) => (acc += cur.cash_registers_limit), 0) || 0)
                : 10000
            const cashRegistersCurrent = CashRegister.all().length
            const cashRegistersLimitReached = cashRegistersLimit <= cashRegistersCurrent

            if(cashRegistersLimitReached) cashRegistersError = 'limit_reached'
            else if(isWeb && (locale === 'sr' || locale === 'ba')) cashRegistersError = 'mobile_needed'

            let orderingDevicesError = ''
            const orderingDevicesLimit = this.tenant.venue.settings?.ordering_devices_limit || 0
            const orderingDevicesCurrent = Terminal.all().filter(t => t.isOrderingDevice()).length
            const orderingDevicesLimitReached = orderingDevicesLimit <= orderingDevicesCurrent

            if(!this.modules.ordering_devices) orderingDevicesError = 'subscription_needed'
            else if(orderingDevicesLimitReached) orderingDevicesError = 'limit_reached'
            else if(!isMobile) orderingDevicesError = 'mobile_needed'

            let kdsError = ''
            const kdsLimit = 1
            const kdsCurrent = Terminal.all().filter(t => t.isKitchenDisplaySystem()).length
            const kdsLimitReached = kdsLimit <= kdsCurrent

            if(!this.modules.kds) kdsError = 'subscription_needed'
            else if(kdsLimitReached) kdsError = 'limit_reached'

            return [
                { type: Terminal.TYPE_CASH_REGISTER, enabled: true, error: cashRegistersError },
                { type: Terminal.TYPE_ORDERING_DEVICE, enabled: !isAllegra && cashRegistersCurrent >= 1, error: orderingDevicesError },
                { type: Terminal.TYPE_KITCHEN_DISPLAY_SYSTEM, enabled: !isAllegra && cashRegistersCurrent >= 1, error: kdsError }
            ]
        },

        enableRegister() {
            return this.allows('allow_cash_register')
                && this.terminalTypes.length > 0
                && !this.terminalForm.$busy
                && !this.cashRegisterForm.$busy
                && !this.terminalTypes.some(t => t.type === this.selectedType && !!t.error)
        }
    },

    methods: {
        createCashRegisterForm() {
            return new Form(this, 'cashRegisterForm', {
                name: this.$t('terminals.cash_register') + ' ' + (CashRegister.all().length + 1),
                activation_code: null,
                reseller_hash: null,
                limit_payments: false,
                uuid: this.device?.uuid || null,
                platform: this.device?.platform || null,
                manufacturer: this.device?.manufacturer || null,
                model: this.device?.model || null
            })
        },

        createTerminalForm() {
            const type = this.selectedType || Terminal.TYPE_CASH_REGISTER

            const terminals = {
                cashRegister: Terminal.all().filter(t => t.isCashRegister()),
                orderingDevice: Terminal.all().filter(t => t.isOrderingDevice()),
                kitchenDisplaySystem: Terminal.all().filter(t => t.isKitchenDisplaySystem()),
            }

            return new Form(this, 'terminalForm', {
                name: this.getTerminalTypeLabel(type) + ' ' + (terminals[type].length + 1),
                key: type + (Math.max(...Terminal.all().map(t => t.id)) + 1),
                type: type,
                cash_register_id: null,
                uuid: this.device?.uuid || null,
                platform: this.device?.platform || null,
                manufacturer: this.device?.manufacturer || null,
                model: this.device?.model || null
            })
        },

        register() {
            if(this.selectedType === Terminal.TYPE_CASH_REGISTER && !this.cashRegisterForm.validate()) return
            if(this.selectedType !== Terminal.TYPE_CASH_REGISTER && !this.terminalForm.validate()) return

            this.$confirm(
                this.$t('terminals.registration_warning', { value: this.getTerminalTypeLabel(this.selectedType) }),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonCancelText: this.$t('general.cancel'),
                    icon: 'fal fa-cash-register',
                    color: 'error',
                    title: this.getTerminalTypeLabel(this.selectedType),
                    width: 370
                },
                async result => {
                    if (!result) return;

                    if(this.selectedType === Terminal.TYPE_CASH_REGISTER) this.registerCashRegister()
                    else this.registerTerminal()
                }
            );
        },

        async registerCashRegister() {
            try {
                const response = await ApiService.post('/cash-registers', this.cashRegisterForm)
                this.showDialog = false

                CashRegisterService.setCashRegister(response.data)

                this.eventBus.$emit('orders-refresh')
                this.$snackbar(this.$t('terminals.registration_success'), 'success')
            } catch (error) {
                const errorCode = error.response?.data?.errors?.[0].code || null
                if(errorCode) this.$snackbar(this.$t(`cash_register.error.${errorCode}`), 'error')
                else this.$snackbar(this.$t(`terminals.registration_failed`), 'error')
            }
        },

        async registerTerminal() {
            try {
                const response = await ApiService.post('/terminals', this.terminalForm)
                this.showDialog = false

                TerminalService.setTerminal(response.data.terminal)

                if(this.selectedType === Terminal.TYPE_KITCHEN_DISPLAY_SYSTEM) {
                    this.$router.push({ name: 'kitchen' }).catch(err => {});
                    return
                }

                if(this.selectedType === Terminal.TYPE_ORDERING_DEVICE) {
                    CashRegisterService.setCashRegister(response.data.cashRegister);

                    const printConfig = await PrintService.getConfig()
                    printConfig.local.printLocally = false;
                    printConfig.local.isPrintServer = false;
                    PrintService.configure(printConfig)

                    this.eventBus.$emit('orders-refresh')
                    this.$snackbar(this.$t('terminals.registration_success'), 'success')

                    setTimeout(() => location.reload(), 1500)
                }
            } catch (error) {
                console.log(error)

                if(this.terminalForm.$errors.name?.length || this.terminalForm.$errors.cash_register_id?.length) return

                if(this.terminalForm.$errors.uuid?.length) {
                    this.terminalForm.setErrors({ name: [this.$t('terminals.already_registered')] })
                }

                this.$snackbar(this.$t(`terminals.registration_failed`), 'error')
            }
        },

        logout() {
            UserService.logout();
            this.$router.push({ name: 'userLogin' }).catch(err => {});
        },

        getTerminalTypeLabel(type) {
            if(type === 'cashRegister') return this.$t('terminals.cash_register')
            else if(type === 'orderingDevice') return this.$t('terminals.ordering_device')
            return this.$t('terminals.kitchen_display_system')
        }
    }
};
</script>

<style lang="scss" scoped>
.header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.terminal-register-content {
    padding: 12px 20px;
    min-height: 270px;

    .tabs {
        .first-tab {
            margin-left: 0 !important;
        }

        ::v-deep .v-slide-group__prev {
            display: none !important;
        }

        ::v-deep .v-tabs-bar__content {
            justify-content: center !important;
        }
    }
}

.v-menu {
    z-index: 1000;
}
</style>
